import React, {useEffect} from 'react';

import {useGate, useUnit} from 'effector-react';

import {TFunction} from 'i18next';

import {useTranslation} from 'react-i18next';

import {useLocation} from 'react-router-dom';

import {ROUTES} from '@app/routing';
import {CounterpartiesGate, counterpartiesModel} from '@entities/counterparties/model';
import {CounterpartiesRawRow} from '@entities/counterparties/types';
import {Control} from '@pages/Counterparties/ui/Control';
import {GridFieldConfig} from '@shared/types/grid';
import {mapTableColumns, Table} from '@shared/ui/Table2.0';
import {formatNumberWithFraction} from '@utils/number';

const config = (t: TFunction): GridFieldConfig<keyof CounterpartiesRawRow>[] => {
    const translate = (key: string) => t('page_contractor.view.' + key);

    return [
        {
            id: 'taxNumber',
            header: translate('tin'),
            isSortable: true,
            width: 200,
        },
        {
            id: 'invoicesCount',
            header: translate('qnt'),
            isSortable: true,
            width: 150,
        },
        {
            id: 'exclusiveAmountSum',
            header: translate('tax_sum_without_taxes'),
            width: 200,
        },
        {
            id: 'vatSum',
            header: translate('tax_sum'),
            maxWidth: 150,
        },
        {
            id: 'payableAmountSum',
            header: translate('sum'),
            maxWidth: 150,
        },
    ];
};

const dataMapper = (raw?: CounterpartiesRawRow[]): Record<keyof CounterpartiesRawRow, any>[] => {
    if (!raw?.length) return [];
    return raw.map((row: CounterpartiesRawRow) => ({
        ...row,
        exclusiveAmountSum: formatNumberWithFraction(row.exclusiveAmountSum),
        vatSum: formatNumberWithFraction(row.vatSum),
        payableAmountSum: formatNumberWithFraction(row.payableAmountSum),
    }));
};

export const CounterpartiesList = () => {
    const {t} = useTranslation();
    const counterparties = useUnit(counterpartiesModel);
    const location = useLocation();

    useEffect(() => {
        counterparties.typeChanged(
            location.pathname === ROUTES.COUNTERPARTY_SELLERS ? 'SELLERS' : 'BUYERS',
        );
    }, [location.pathname]);

    useGate(CounterpartiesGate, {type: counterparties.state.type});

    const data = React.useMemo(() => dataMapper(counterparties.state.data?.items?.data), [
        t,
        counterparties.state.data,
    ]);
    const columns = React.useMemo(
        () =>
            mapTableColumns(
                config(t),
                counterparties.state.filters,
                counterparties.filtersChanged,
                counterparties.filtersReset,
                counterparties.state.sortings,
                counterparties.sortingsChanged,
            ),
        [t, counterparties.state.filters, counterparties.state.sortings],
    );

    return (
        <Table
            headerControls={<Control />}
            tableTabsSettings={{
                baseUrl: '/counterparties',
                links: [
                    {label: t('page_contractor.sellers'), path: 'sellers'},
                    {label: t('page_contractor.buyers'), path: 'buyers'},
                ],
            }}
            columns={columns}
            data={data}
            isLoading={counterparties.state.pending}
            hasNext={Boolean(counterparties.state.data?.nextPageRef)}
            pageIndex={counterparties.state.pageNumber}
            onPageChange={counterparties.pageNumberChanged}
            onPageSizeChange={counterparties.pageSizeChanged}
        />
    );
};
